import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { style } from './link.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { fontStyles, fontVariantsType } from 'elements/_shared/fonts.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { ifDefined } from 'lit/directives/if-defined.js'


export const linkTargets = ['_self', '_blank', '_parent', '_top'] as const
export type LinkTargetTypes = typeof linkTargets

export const linkVariants = ['primary', 'secondary', 'white'] as const
export type LinkVariantTypes = typeof linkVariants

@customElement('cse-link')
export class CSELink extends LitElement {
  static override styles: CSSResultGroup = [fontStyles, hostStyle, style]

  @property({ type: String })
  variant: LinkVariantTypes[number] = 'primary'

  @property({ type: String, attribute: 'font-variant' })
  fontVariant: fontVariantsType[number] = 'body'

  @property()
  url = ''

  @property()
  target: LinkTargetTypes[number] = '_self'

  @property()
  rel = ''

  @property({ type: String })
  download = ''

  @property({ type: Boolean })
  disabled = false

  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel = ''

  private handleClick(e: MouseEvent) {
    if (this.disabled) e.preventDefault()
  }

  override render() {
    const classes = classMap({
      'cse-link': true,
      [`variant-${this.variant}`]: true,
      [`font-${this.fontVariant}`]: true,
      'disabled': this.disabled,
    })
    if (this.url !== '') {
      return html`
        <a
          @click=${this.handleClick}
          href=${this.url}
          target=${this.target}
          class="${classes}"
          tabindex="0"
          aria-label="${this.ariaLabel}"
          rel=${ifDefined(this.rel || undefined)}
          download=${ifDefined(this.download.trim() ? this.download : undefined)}
          ?disabled=${this.disabled}
        >
          <slot name="prefix"></slot>
          <slot class="link-text"></slot>
          <slot name="suffix"></slot>
        </a>
      `
    } else {
      return html`
        <button 
          @click=${this.handleClick} 
          class="${classes}" 
          aria-label="${this.ariaLabel}" 
          ?disabled=${this.disabled}
        >
          <slot name="prefix"></slot>
          <slot class="link-text"></slot>
          <slot name="suffix"></slot>
        </button>
      `
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-link': CSELink
  }
}
